// Contains the base class which each repository should extend. It implements
// some checkers that should be present on each repository, making life easier
// for the developer that needs to create their own type of repository.
import type {
    IRepository,
    IFields,
    RepositoryAction,
    IRepositoryManager,
    IRepositoryPropertiesChecker,
} from "@/wc/registry/lib/service/repository/types.js";

import type { IRepositoryParameters } from "@/wc/registry/lib/service/repository/types";

import config from "@/config";

export class RepositoryEventTarget extends EventTarget {
    repositoryName = undefined;
    constructor(repositoryName) {
        super();
        this.repositoryName = repositoryName;
    }
}

class NotImplementedError extends Error {
    constructor(message = "", ...args) {
        super(message, ...args);
        this.message = message + " has not yet been implemented.";
    }
}

export class InsufficientPropsError extends Error {
    missingProp: string;
    constructor(message = "", missingProp = "", ...args) {
        super(message, ...args);
        this.missingProp = missingProp;
    }
}

export class RepositoryDoesNotSupportActionError extends Error {}

export class BaseRepositoryManager implements IRepositoryManager {
    repository: IRepository;
    constructor(repository: IRepository) {
        this.repository = repository;
    }

    // Below function is something that can be called by components or
    // repositories to check if they have all the required properties to be
    // able to execute a certain action.
    test(action: RepositoryAction, props: IRepositoryParameters) {
        if (!this.repository.actionPaths[action]) {
            throw Error(
                `Repository '${this.repository.name}' does not have the '${action}' path configured in its paths or '${action}' is mapped to another name through a custom action configuration.`
            );
        }

        // When there are no required path variables for this action, the request
        // will probably work.
        if (!("requiredParameters" in this.repository.actionPaths[action])) {
            return true;
        }

        this.repository.actionPaths[action].requiredParameters.forEach(
            (variable: string) => {
                if (!props || !(variable in props)) {
                    throw new InsufficientPropsError(
                        `Repository '${this.repository.name}' needs the '${variable}' passed as a prop for this action.`,
                        variable
                    );
                }
            }
        );
    }

    // Replace e.g. /producer/{producer_id} with /producer/5, when producer_id
    // is passed in the props.
    buildRequestUrl(
        action: RepositoryAction,
        props: IRepositoryParameters = {}
    ) {
        this.test(action, props);
        let url = config.bekendURI + this.repository.actionPaths[action].path;
        const requiredParameters =
            this.repository.actionPaths[action].requiredParameters;
        const allParameters = this.repository.actionPaths[action].allParameters;

        requiredParameters.forEach((parameter) => {
            if (parameter in props) {
                url = url.replace(`{${parameter}}`, `${props[parameter]}`);
            }
        });

        // Check if one of the possible query parameters is passed as a prop,
        // if so, add it to the URL.
        const shouldAddSearchParam = allParameters.queryParameters.some(
            (queryParam) => Object.keys(props).includes(queryParam)
        );
        if (shouldAddSearchParam) {
            const searchParams = new URLSearchParams();
            allParameters.queryParameters.forEach((queryParam) => {
                if (!(queryParam in props)) {
                    return;
                }

                const valueOrValues = props[queryParam];
                if (Array.isArray(valueOrValues)) {
                    valueOrValues.forEach((value) => {
                        searchParams.append(queryParam, `${value}`);
                    });
                } else {
                    searchParams.append(queryParam, `${valueOrValues}`);
                }
            });
            url = url + "?" + searchParams.toString();
        }

        return url;
    }

    // Note that we have to recreate the response data after reading it, so it
    // can be read again.
    async handleResponse(
        action: RepositoryAction,
        response: Response
    ): Promise<Response> {
        const contentType = response.headers.get("content-type");
        const eventOptions = {};

        if (contentType === "application/json") {
            const json = await response.json();
            response["json"] = async () => json;
            eventOptions["detail"] = json;
        }

        let eventName = action + "d";
        if (action === "partialUpdate") {
            eventName = "patched";
        }

        this.repository.events.dispatchEvent(
            new CustomEvent(eventName, eventOptions)
        );
        this.repository.events.dispatchEvent(
            new CustomEvent(`repository:${eventName}`, eventOptions)
        );
        return response;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    get(props: IRepositoryParameters, fields: IFields) {
        throw new NotImplementedError(`repository.get()`);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    all(props: IRepositoryParameters, fields: IFields) {
        throw new NotImplementedError(`repository.all()`);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    update(props: IRepositoryParameters, fields: IFields) {
        throw new NotImplementedError(`repository.update()`);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    partialUpdate(props: IRepositoryParameters, fields: IFields) {
        throw new NotImplementedError(`repository.partialUpdate()`);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    create(props: IRepositoryParameters, fields: IFields) {
        throw new NotImplementedError(`repository.create()`);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    delete(props: IRepositoryParameters) {
        throw new NotImplementedError(`repository.delete()`);
    }
}

// To allow consumers to inspect the response properties for a given action.
class ResponsePropertiesChecker {
    constructor(repository: IRepository) {
        this.repository = repository;
    }

    get get() {
        return this.repository.actionPaths["get"].response;
    }

    get all() {
        return this.repository.actionPaths["all"].response;
    }

    get update() {
        return this.repository.actionPaths["update"].response;
    }

    get partialUpdate() {
        return this.repository.actionPaths["partialUpdate"].response;
    }

    get create() {
        return this.repository.actionPaths["create"].response;
    }

    get delete() {
        return this.repository.actionPaths["delete"].response;
    }
}

export class BaseRepositoryPropertiesChecker
    implements IRepositoryPropertiesChecker
{
    repository: IRepository;
    constructor(repository: IRepository) {
        this.repository = repository;
        this.response = new ResponsePropertiesChecker(repository);
    }

    test(action: string) {
        if (!this.repository.actionPaths[action]) {
            throw new RepositoryDoesNotSupportActionError(
                `Repository '${this.repository.name}' does not have the '${action}' path configured in its paths.`
            );
        }

        // When there are no required path variables for this action, the request
        // will probably work.
        if (!("properties" in this.repository.actionPaths[action])) {
            throw Error(
                `Repository '${this.repository.name}' does not define any properties for the '${action}' action.`
            );
        }
    }

    get get() {
        this.test("get");
        return this.repository.actionPaths["get"].properties;
    }

    get all() {
        this.test("all");
        return this.repository.actionPaths["all"].properties;
    }

    get update() {
        this.test("update");
        return this.repository.actionPaths["update"].properties;
    }

    get partialUpdate() {
        this.test("partialUpdate");
        return this.repository.actionPaths["partialUpdate"].properties;
    }

    get create() {
        this.test("create");
        return this.repository.actionPaths["create"].properties;
    }

    get delete() {
        this.test("delete");
        return this.repository.actionPaths["delete"].properties;
    }
}
